/** XeAgents.ai **/
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Users,
  TrendingUp,
  Crown,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";

// Backend response structure for seat usage
interface SeatUsage {
  currentSeats: number;
  maxSeats: number;
  availableSeats: number;
  isAtLimit: boolean;
  planName: string;
  canAddMore: boolean;
  additionalSeatPrice?: string;
  additionalSeatPriceRazorpay?: string;
}

// Backend response for /api/organizations/seats/statistics
interface SeatStatisticsResponse {
  usage: SeatUsage;
  plan: {
    id: string;
    name: string;
    displayName: string;
    maxSeats: number;
  } | null;
  subscription: {
    id: string;
    planId: string;
    status: string;
  } | null;
  recommendations: {
    shouldUpgrade: boolean;
    canAddMembers: boolean;
    utilizationPercentage: number;
  };
}

// Transformed seat statistics for UI
interface SeatStatistics {
  usedSeats: number;
  totalSeats: number;
  percentageUsed: number;
  canAddMore: boolean;
  planName: string;
  planType: string;
  maxSeatsInPlan: number;
  additionalSeatPrice: string;
  additionalSeatPriceRazorpay: string;
}

interface UpgradeOption {
  type: string;
  planId?: string;
  planName?: string;
  description: string;
  cost?: number;
  currency?: string;
}

export default function BillingAndSeats() {
  const { t } = useTranslation();

  // Fetch seat statistics from backend
  const { data: seatStatsResponse, isLoading: statsLoading } = useQuery<SeatStatisticsResponse>({
    queryKey: ["/api/organizations/seats/statistics"],
  });

  // Transform seat statistics to expected format
  const seatStats: SeatStatistics | undefined = seatStatsResponse?.usage ? {
    usedSeats: seatStatsResponse.usage.currentSeats,
    totalSeats: seatStatsResponse.usage.maxSeats,
    percentageUsed: seatStatsResponse.recommendations?.utilizationPercentage ?? 
      Math.round((seatStatsResponse.usage.currentSeats / seatStatsResponse.usage.maxSeats) * 100),
    canAddMore: seatStatsResponse.usage.canAddMore,
    planName: seatStatsResponse.plan?.displayName ?? seatStatsResponse.usage.planName ?? "Free",
    planType: seatStatsResponse.plan?.name ?? "free",
    maxSeatsInPlan: seatStatsResponse.usage.maxSeats,
    additionalSeatPrice: seatStatsResponse.usage.additionalSeatPrice ?? "10.00",
    additionalSeatPriceRazorpay: seatStatsResponse.usage.additionalSeatPriceRazorpay ?? "",
  } : undefined;

  // Fetch upgrade options
  const { data: upgradeOptions, isLoading: optionsLoading } = useQuery<UpgradeOption[]>({
    queryKey: ["/api/organizations/upgrade-options"],
  });

  const isLoading = statsLoading || optionsLoading;

  const availableSeats = (seatStats?.totalSeats || 0) - (seatStats?.usedSeats || 0);
  const seatUsagePercent = seatStats?.percentageUsed || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing & Seats</h1>
        <p className="text-muted-foreground mt-1">
          Manage your organization's seat allocation and billing
        </p>
      </div>

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Crown className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Current Plan</CardTitle>
                <CardDescription>Your active subscription plan</CardDescription>
              </div>
            </div>
            <Link href="/app/upgrade">
              <Button variant="outline">
                <TrendingUp className="h-4 w-4 mr-2" />
                Upgrade Plan
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-64" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-bold">{seatStats?.planName || "Free"}</h3>
                <Badge variant={seatStats?.planType === "pro" ? "default" : "secondary"}>
                  {seatStats?.planType || "free"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Includes {seatStats?.maxSeatsInPlan || 1} team {(seatStats?.maxSeatsInPlan || 1) === 1 ? "seat" : "seats"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seat Usage Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Seat Usage
          </CardTitle>
          <CardDescription>
            Track your team member seat allocation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <>
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-48" />
            </>
          ) : (
            <>
              {/* Usage Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Seats</p>
                  <p className="text-3xl font-bold">{seatStats?.totalSeats || 0}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Used Seats</p>
                  <p className="text-3xl font-bold">{seatStats?.usedSeats || 0}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Available Seats</p>
                  <p className="text-3xl font-bold">{availableSeats}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Seat Usage</span>
                  <span className="font-medium">{Math.round(seatUsagePercent)}%</span>
                </div>
                <Progress value={seatUsagePercent} className="h-3" />
              </div>

              {/* Status Alert */}
              {seatUsagePercent >= 80 && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Seats running low</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You&apos;re using {seatStats?.usedSeats} of {seatStats?.totalSeats} seats. 
                      Consider upgrading your plan for more seats.
                    </p>
                  </div>
                </div>
              )}

              {seatUsagePercent < 80 && availableSeats > 0 && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">You have available seats</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You can invite {availableSeats} more {availableSeats === 1 ? "member" : "members"} to your organization.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Options */}
      {upgradeOptions && upgradeOptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recommended Upgrades
            </CardTitle>
            <CardDescription>
              Based on your current usage, consider these options
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {upgradeOptions.map((option, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{option.planName || option.type}</p>
                        {option.cost && (
                          <Badge variant="secondary">
                            {option.currency === "USD" ? "$" : "₹"}
                            {option.cost}/month
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                    <Link href="/app/upgrade">
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
}